import React from "react"
import { Box } from "@material-ui/core"

import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/GenericConditionSectionContainer/ClientBoardStageContainer/styles"
import { CustomValidationRenderValue } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/GenericConditionSectionContainer"
import {
	VALIDATION_TYPE_BOARD_CONDITION_TO_NAME,
	isContactValueValidation,
	isOpportunityStatusValidation,
	isLossReasonValidation,
	getContactValueOperator,
	getOpportunityStatusOperator,
	getOpportunityStatusValue,
	getLossReasonId,
	contactValueOperatorToLabel,
	OpportunityStatusOperator,
	OpportunityStatusValue
} from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/GenericConditionSectionEditor/ClientBoardStageInput"
import { ChatBotFlowBlockRuleValidation } from "@/protocols/chatBot"
import { Divider } from "@/components"
import { colors } from "@/styles/colors"
import { convertCentsInReals } from "@/utils/currencyFormatter"

const OPPORTUNITY_STATUS_OPERATOR_TO_LABEL: Record<OpportunityStatusOperator, string> = {
	equal: "Igual",
	not_equal: "Não igual"
}

const OPPORTUNITY_STATUS_VALUE_TO_LABEL: Record<OpportunityStatusValue, string> = {
	won: "Ganho",
	lost: "Perdido"
}

type LossReasonChipsProps = {
	lossReasonText: string | undefined
}

const LossReasonChips: React.FC<LossReasonChipsProps> = ({ lossReasonText }) => {
	const classes = useStyles()

	return (
		<Box className={classes.lossReasonGroup}>
			<ContrastChip
				title="Motivo da perda"
				backgroundColor={colors.blue[200]}
			/>
			<ContrastChip
				title={lossReasonText ?? "??"}
				backgroundColor={lossReasonText ? colors.blue[400] : colors.blue[200]}
				className={classes.lossReasonValueChip}
			/>
		</Box>
	)
}

const buildContactValueOnlyText = (validation: ChatBotFlowBlockRuleValidation): string => {
	const operator = getContactValueOperator(validation)

	if (operator === "between") {
		const fromValueInCents = validation.deep_validations?.[0]?.second_param as number | "" | undefined
		const toValueInCents = validation.deep_validations?.[1]?.second_param as number | "" | undefined

		if (fromValueInCents === undefined || fromValueInCents === "" || toValueInCents === undefined || toValueInCents === "") {
			return "??"
		}

		return `${convertCentsInReals(Number(fromValueInCents))} – ${convertCentsInReals(Number(toValueInCents))}`
	}

	const valueInCents = validation.deep_validations?.[0]?.second_param as number | "" | undefined

	if (valueInCents === undefined || valueInCents === "") {
		return "??"
	}

	return convertCentsInReals(Number(valueInCents))
}

const ClientBoardStageContainer: CustomValidationRenderValue = {
	firstItem: {
		render: ({ validation, constructionResources }) => {
			const boardSelected = constructionResources.conditionOptions.boards?.find((board) => board.id === Number(validation.extra_data?.board_stage_condition?.board_id))

			return (
				<>
					<ContrastChip
						title={"Funil"}
						backgroundColor={colors.blue[200]}
					/>
					{
						boardSelected
							? <ContrastChip
								title={boardSelected?.name}
								backgroundColor={colors.blue[400]}
								style={{ maxWidth: 180 }}
							/>

							: <ContrastChip
								title="??"
								backgroundColor={colors.blue[200]}
							/>
					}
				</>
			)
		}
	},
	secondItem: {
		render: ({ validation, constructionResources }) => {
			const isLossReason = isLossReasonValidation(validation)
			const isOpportunityStatus = isOpportunityStatusValidation(validation)
			const isContactValue = isContactValueValidation(validation)

			if (isLossReason) {
				const lossReasonId = getLossReasonId(validation)
				const boardSelected = constructionResources.conditionOptions.boards?.find((board) => board.id === Number(validation.extra_data?.board_stage_condition?.board_id))
				const lossReasonSelected = boardSelected?.lossReasons?.find((reason) => reason.id === lossReasonId)
				const isLossReasonOther = lossReasonId === 0
				const lossReasonText = isLossReasonOther ? "Outro" : lossReasonSelected?.reasonText

				return <LossReasonChips lossReasonText={lossReasonText} />
			}

			if (isOpportunityStatus) {
				const operator = getOpportunityStatusOperator(validation)
				const statusValue = getOpportunityStatusValue(validation)

				return (
					<>
						<ContrastChip
							title="Status do contato"
							backgroundColor={colors.blue[200]}
						/>
						<ContrastChip
							title={OPPORTUNITY_STATUS_OPERATOR_TO_LABEL[operator]}
							backgroundColor={colors.blue[200]}
						/>
						<Divider orientation="horizontal" size={0.5} />
						<ContrastChip
							title={OPPORTUNITY_STATUS_VALUE_TO_LABEL[statusValue]}
							backgroundColor={colors.blue[400]}
						/>
					</>
				)
			}

			if (isContactValue) {
				const operator = getContactValueOperator(validation)
				const operatorLabel = contactValueOperatorToLabel[operator]
				const valueText = buildContactValueOnlyText(validation)
				const hasValue = valueText !== "??"

				return (
					<>
						<ContrastChip
							title="Valor do contato"
							backgroundColor={colors.blue[200]}
						/>
						<ContrastChip
							title={operatorLabel}
							backgroundColor={colors.blue[200]}
						/>
						<Divider orientation="horizontal" size={0.5} />
						<ContrastChip
							title={valueText}
							backgroundColor={hasValue ? colors.blue[400] : colors.blue[200]}
						/>
					</>
				)
			}

			const validationType = VALIDATION_TYPE_BOARD_CONDITION_TO_NAME[validation.type]
			const boardSelected = constructionResources.conditionOptions.boards?.find((board) => board.id === Number(validation.extra_data?.board_stage_condition?.board_id))
			const stageSelected = boardSelected?.stages.find((stage) => stage.id === validation.second_param)

			return (
				<>
					<ContrastChip
						title={validationType}
						backgroundColor={colors.blue[200]}
					/>
					<ContrastChip
						title={stageSelected?.name ?? "??"}
						backgroundColor={stageSelected ? colors.blue[400] : colors.blue[200]}
					/>
				</>
			)
		}
	},
	comparisonItem: {
		render: () => {
			return (
				<></>
			)
		}
	}
}

export default ClientBoardStageContainer
